// src/pages/GameMenu.jsx
// Piano With Pan — Game Menu V13
// ✅ V4: Added 📊 Progress button to header (links to /progress parent report)
//
// All other V13 logic preserved: emoji badges, Pan+Wis companions, premium
// gating, cross-device sync, loading state, GA tracking.

import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TrophyStorage } from "../services/trophyStorage";
import { getMaxUnlockedLevel } from "../services/unlockRules";
import { usePremiumStatus } from "../hooks/usePremiumStatus";
import { getStreakStatus, dailyGoalPct } from "../utils/progressTracker"; // ✅ V4

/* ══ GA ══ */
const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== "undefined" && window.gtag) window.gtag("event", eventName, parameters);
};
const trackPageView = (page) => {
  if (typeof window !== "undefined" && window.gtag) window.gtag("config", "G-TZC5XT1878", { page_path: page });
};

/* ── Flags ── */
export const TUTORIAL_KEY = "pwp_tutorial_done_v1";
const INTRO_SEEN_KEY = "pwp_intro_video_seen_v1";

export function setTutorialDone() { try { localStorage.setItem(TUTORIAL_KEY, "1"); } catch {} }
export function isTutorialDone() { try { return !!localStorage.getItem(TUTORIAL_KEY); } catch { return false; } }
function isIntroSeen() { try { return !!localStorage.getItem(INTRO_SEEN_KEY); } catch { return false; } }
function markIntroSeen() { try { localStorage.setItem(INTRO_SEEN_KEY, "1"); } catch {} }

/* ── Companion images ── */
const PAN_IMG = "/images/pan-the-panda.png";
const WIS_IMG = "/images/Wis_.png";

/* ── Levels (themed emojis) ── */
const LEVELS = [
  { id: 1,  song: "Twinkle Twinkle",       tier: "free",    emoji: "⭐" },
  { id: 2,  song: "Happy Birthday",        tier: "free",    emoji: "🦓" },
  { id: 3,  song: "Ode to Joy",            tier: "free",    emoji: "🐧" },
  { id: 4,  song: "Machli Jal Ki Rani",    tier: "premium", emoji: "🐠" },
  { id: 5,  song: "Jingle Bells",          tier: "premium", emoji: "🔔" },
  { id: 6,  song: "Incey Wincey Spider",   tier: "premium", emoji: "🕷️" },
  { id: 7,  song: "London Bridge",         tier: "premium", emoji: "🌉" },
  { id: 8,  song: "National Anthem",       tier: "premium", emoji: "🦚" },
  { id: 9,  song: "Row Row Row Your Boat", tier: "premium", emoji: "🚣" },
  { id: 10, song: "Ancient Melody",        tier: "premium", emoji: "🐉", isBoss: true },
];

const INTRO_VIDEO_SRC = "/videos/Treble Town Landing Page Video.mp4";
const INTRO_VIDEO_POSTER = "/images/PanWisHead.png";

/* ─────────────────────────────────────────────
   INTRO VIDEO MODAL
───────────────────────────────────────────── */
function IntroVideoOverlay({ onDone, source = "auto" }) {
  const videoRef = useRef(null);

  const handleClose = useCallback(() => {
    trackEvent("intro_video_closed", {
      source,
      video_completed: videoRef.current?.ended || false,
      current_time: videoRef.current?.currentTime || 0,
    });
    if (source === "auto") markIntroSeen();
    onDone();
  }, [onDone, source]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  useEffect(() => {
    trackEvent("intro_video_shown", { source });
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose, source]);

  return (
    <div className="gm-intro-overlay" onClick={handleBackdropClick}>
      <div className="gm-intro-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gm-intro-close" onClick={handleClose} aria-label="Close" type="button">×</button>
        <div className="gm-intro-video-wrap">
          <video ref={videoRef} src={INTRO_VIDEO_SRC} autoPlay playsInline controls
            className="gm-intro-video" onEnded={handleClose} onError={handleClose} />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPANIONS — Pan + Wis flank the active node
───────────────────────────────────────────── */
function Companions({ size = 56 }) {
  return (
    <div className="gm-companions" style={{ "--companion-size": `${size}px` }}>
      <img src={PAN_IMG} alt="" className="gm-companion gm-companion-pan" aria-hidden="true" draggable={false} />
      <img src={WIS_IMG} alt="" className="gm-companion gm-companion-wis" aria-hidden="true" draggable={false} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   LEVEL NODE
───────────────────────────────────────────── */
function LevelNode({ level, isCompleted, isUnlocked, isNext, isPremiumLocked, isLoading, navigate, premiumLoaded, showCompanions }) {
  let nodeStatus = "locked";
  if (isCompleted) nodeStatus = "done";
  else if (isLoading) nodeStatus = "loading";
  else if (isPremiumLocked) nodeStatus = "premium-locked";
  else if (isNext) nodeStatus = "active";
  else if (isUnlocked) nodeStatus = "unlocked";

  const isDisabled = isLoading || (!isUnlocked && !isPremiumLocked);

  const handleClick = () => {
    if (isLoading) { trackEvent("level_click_premium_loading", { level_id: level.id }); return; }
    trackEvent("level_click", {
      level_id: level.id, level_song: level.song, tier: level.tier,
      is_unlocked: isUnlocked, is_premium_locked: isPremiumLocked,
      is_completed: isCompleted, is_loading: isLoading,
    });
    if (isPremiumLocked) { trackEvent("premium_level_blocked", { level_id: level.id }); navigate("/pwp-paywall?reason=premium_required"); return; }
    navigate(`/level-${level.id}`);
  };

  const isBoss = level.isBoss;
  const emojiDim = !isUnlocked || isPremiumLocked || isLoading;

  return (
    <div className={`gm-node-wrap ${showCompanions ? "gm-node-wrap-with-companions" : ""} ${isBoss ? "gm-node-wrap-boss" : ""}`}>
      {showCompanions && <Companions size={isBoss ? 64 : 56} />}
      <button type="button" onClick={handleClick} disabled={isDisabled}
        className={`gm-node gm-node-${nodeStatus} ${isBoss ? "gm-node-boss" : ""}`}
        aria-label={`Level ${level.id}: ${level.song} ${isLoading ? "(checking access)" : ""}`}>
        <div className={`gm-circle gm-circle-${nodeStatus} ${isBoss ? "gm-circle-boss" : ""}`}>
          {isCompleted ? (
            <span className="gm-circle-icon">{isBoss ? "🏆" : "✓"}</span>
          ) : isLoading ? (
            <span className="gm-circle-icon gm-circle-loading-spinner">⏳</span>
          ) : !isUnlocked && !isPremiumLocked ? (
            <span className="gm-circle-icon">🔒</span>
          ) : (
            <>
              <span className="gm-circle-num">{level.id}</span>
              {isPremiumLocked && <span className="gm-circle-lock">🔒</span>}
            </>
          )}
          {isNext && !isPremiumLocked && !isLoading && (
            <span className="gm-circle-pulse" aria-hidden="true" />
          )}
          {!isCompleted && (
            <span className={`gm-circle-emoji ${emojiDim ? "gm-circle-emoji-dim" : ""}`} aria-hidden="true">
              {level.emoji}
            </span>
          )}
        </div>
        <div className="gm-node-song">{level.song}</div>
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   LEARN NOTES NODE
───────────────────────────────────────────── */
function LearnNotesNode({ tutorialDone, navigate, showCompanions }) {
  const handleClick = () => {
    trackEvent("learn_notes_click", { source: "path_node", dest: "/lyn-game/1" });
    navigate("/lyn-game/1");
  };

  return (
    <div className={`gm-node-wrap ${showCompanions ? "gm-node-wrap-with-companions" : ""}`}>
      {showCompanions && <Companions size={56} />}
      <button type="button" onClick={handleClick}
        className={`gm-node gm-node-learn ${!tutorialDone ? "gm-node-learn-pulse" : ""}`}
        aria-label={tutorialDone ? "Learn your notes (revisit)" : "Learn your notes — tap to start"}>
        <div className={`gm-circle gm-circle-learn ${tutorialDone ? "gm-circle-learn-done" : ""}`}>
          <span className="gm-circle-icon">📖</span>
          {!tutorialDone && (
            <span className="gm-circle-pulse gm-circle-pulse-green" aria-hidden="true" />
          )}
        </div>
        <div className="gm-node-song">Learn your Notes</div>
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PRACTICE NODE
───────────────────────────────────────────── */
function PracticeNode({ navigate }) {
  const handleClick = () => {
    trackEvent("practice_hub_click", { source: "path_node" });
    navigate("/practice");
  };

  return (
    <div className="gm-node-wrap">
      <button type="button" onClick={handleClick}
        className="gm-node gm-node-practice"
        aria-label="Practice — optional free play and note warm-ups">
        <div className="gm-circle gm-circle-practice">
          <span className="gm-circle-icon">🎹</span>
        </div>
        <div className="gm-node-song">Practice</div>
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DESKTOP WINDING PATH
───────────────────────────────────────────── */
function DesktopWindingPath({ levelStatuses, navigate, premiumLoaded, tutorialDone, activeLevelId, learnNotesIsActive }) {
  const free  = levelStatuses.slice(0, 3);
  const prem1 = levelStatuses.slice(3, 6);
  const prem2 = levelStatuses.slice(6, 9);
  const boss  = levelStatuses[9];

  const HConn = ({ a, b }) => {
    const done = a.isCompleted && b.isCompleted;
    const prem = a.premiumLocked || b.premiumLocked;
    const loading = a.isLoading || b.isLoading;
    return (
      <div className={`gm-dt-h-conn ${done ? "gm-dt-h-conn-done" : loading ? "gm-dt-h-conn-loading" : prem ? "gm-dt-h-conn-prem" : ""}`}
        aria-hidden="true" />
    );
  };

  const renderLevel = (entry) => (
    <LevelNode
      level={entry.level} isCompleted={entry.isCompleted} isUnlocked={entry.isUnlocked}
      isNext={entry.isNext} isPremiumLocked={entry.premiumLocked} isLoading={entry.isLoading}
      navigate={navigate} premiumLoaded={premiumLoaded}
      showCompanions={entry.level.id === activeLevelId}
    />
  );

  const NodeRow = ({ entries, rtl = false }) => (
    <div className={`gm-dt-row ${rtl ? "gm-dt-row-rtl" : ""}`}>
      {entries.map((entry, i) => (
        <React.Fragment key={entry.level.id}>
          {renderLevel(entry)}
          {i < entries.length - 1 && <HConn a={entry} b={entries[i + 1]} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="gm-dt-path">
      <div className="gm-dt-row">
        <LearnNotesNode tutorialDone={tutorialDone} navigate={navigate} showCompanions={learnNotesIsActive} />
        <div className={`gm-dt-h-conn ${tutorialDone ? "gm-dt-h-conn-done" : ""}`} aria-hidden="true" />
        <PracticeNode navigate={navigate} />
      </div>

      <div className="gm-dt-down" aria-hidden="true" />
      <NodeRow entries={free} />
      <div className="gm-dt-down" aria-hidden="true" />
      <NodeRow entries={prem1} />
      <div className="gm-dt-turn gm-dt-turn-right" aria-hidden="true" />
      <NodeRow entries={prem2} rtl />
      <div className="gm-dt-turn gm-dt-turn-left" aria-hidden="true" />

      <div className="gm-dt-boss-zone">
        <div className="gm-dt-boss-tag">⚔️ Final Boss</div>
        {renderLevel(boss)}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function GameMenu() {
  const navigate = useNavigate();
  const { isPremium, premiumLoaded, refresh } = usePremiumStatus();

  const [showIntro, setShowIntro] = useState(() => !isIntroSeen());
  const [introSource, setIntroSource] = useState("auto");

  // ✅ V4: Streak + daily goal for the header badge
  const streakStatus = useMemo(() => {
    try { return getStreakStatus(); } catch { return { count: 0, playedToday: false, status: "fresh" }; }
  }, []);
  const goalPct = useMemo(() => {
    try { return dailyGoalPct(); } catch { return 0; }
  }, []);

  const { tutorialDone, completedLevels, maxUnlocked, totalStars } = useMemo(() => {
    const tutorialDone = isTutorialDone();
    const s = TrophyStorage.getState?.() || {};
    const rip = s?.roundInProgress;
    const doneMap = rip?.levelsCompleted || {};
    const starsMap = rip?.starsCollected || {};
    const completedSet = new Set(Object.keys(doneMap).filter((k) => doneMap[k]).map(Number));
    return {
      tutorialDone,
      completedLevels: completedSet,
      maxUnlocked: getMaxUnlockedLevel(),
      totalStars: Object.values(starsMap).filter(Boolean).length,
    };
  }, []);

  const completedCount = completedLevels.size;

  useEffect(() => {
    trackPageView("/game-menu");
    trackEvent("game_menu_loaded", {
      max_unlocked_level: maxUnlocked, completed_count: completedCount,
      total_stars: totalStars, has_premium: isPremium, premium_loaded: premiumLoaded,
    });
  }, [maxUnlocked, completedCount, totalStars, isPremium, premiumLoaded]);

  useEffect(() => { refresh(); }, [refresh]);

  const levelStatuses = useMemo(() => {
    return LEVELS.map((level) => {
      const isCompleted = completedLevels.has(level.id);
      const isUnlocked = level.id <= maxUnlocked;
      const isNext = level.id === maxUnlocked && !isCompleted;
      const isLoading = level.tier === "premium" && !premiumLoaded;
      const premiumLocked = level.tier === "premium" && !isPremium && premiumLoaded;
      return { level, isCompleted, isUnlocked, isNext, premiumLocked, isLoading };
    });
  }, [completedLevels, maxUnlocked, isPremium, premiumLoaded]);

  const { activeLevelId, learnNotesIsActive } = useMemo(() => {
    if (!tutorialDone) return { activeLevelId: null, learnNotesIsActive: true };
    const first = levelStatuses.find(
      s => !s.isCompleted && s.isUnlocked && !s.premiumLocked && !s.isLoading
    );
    return { activeLevelId: first ? first.level.id : null, learnNotesIsActive: false };
  }, [tutorialDone, levelStatuses]);

  const handleWatchAgain = useCallback(() => {
    trackEvent("watch_intro_again_clicked", { source: "thumbnail" });
    setIntroSource("watch_again");
    setShowIntro(true);
  }, []);

  const handleIntroClosed = useCallback(() => {
    setShowIntro(false);
    setIntroSource("auto");
  }, []);

  // ✅ V4: streak flame label
  const streakLabel = streakStatus.count > 0
    ? `🔥 ${streakStatus.count}`
    : streakStatus.playedToday ? "✅" : "—";

  return (
    <div className="gm-root">
      <style>{CSS}</style>

      {showIntro && <IntroVideoOverlay onDone={handleIntroClosed} source={introSource} />}

      <div className="gm-bg">
        <div className="gm-bg-base" />
        <div className="gm-blob gm-blob-1" />
        <div className="gm-blob gm-blob-2" />
      </div>

      <header className="gm-header">
        <button className="gm-hdr-btn" onClick={() => { trackEvent("nav_home_click"); navigate("/home"); }} aria-label="Home">←</button>
        <div className="gm-hdr-title">
          <span className="gm-hdr-stars">⭐ {totalStars}</span>
          <span className="gm-hdr-progress">{completedCount}/10</span>
        </div>
        <div className="gm-hdr-right">
          {isPremium && premiumLoaded && (
            <button className="gm-hdr-pro" onClick={() => { trackEvent("my_pass_hdr_click"); navigate("/my-pass"); }}
              aria-label="View my premium pass" title="My Pass">
              <span className="gm-hdr-pro-star">⭐</span>
              <span className="gm-hdr-pro-text">PRO</span>
            </button>
          )}
          {/* ✅ V4: Progress button — streak + daily goal */}
          <button
            className="gm-hdr-btn gm-hdr-progress-btn"
            onClick={() => { trackEvent("progress_report_click"); navigate("/progress"); }}
            aria-label="Parent progress report"
            title={`Progress report · ${goalPct}% of today's goal · streak: ${streakStatus.count}`}
          >
            <span style={{ fontSize:"1rem", lineHeight:1 }}>📊</span>
            {streakStatus.count > 0 && (
              <span style={{ fontSize:"0.6rem", fontWeight:800, color:"#ea580c", lineHeight:1 }}>
                {streakLabel}
              </span>
            )}
          </button>
          <button className="gm-hdr-btn" onClick={() => { trackEvent("nav_trophy_cabinet_click"); navigate("/trophy-cabinet"); }} aria-label="Trophies">🏆</button>
        </div>
      </header>

      <main className="gm-main">
        <div className="gm-content">

          <button type="button" onClick={handleWatchAgain} className="gm-video-thumb" aria-label="Watch story again">
            <div className="gm-video-thumb-poster" style={{ backgroundImage: `url(${INTRO_VIDEO_POSTER})` }}>
              <div className="gm-video-thumb-play">▶</div>
            </div>
            <div className="gm-video-thumb-text">
              <div className="gm-video-thumb-title">Watch story again</div>
              <div className="gm-video-thumb-sub">The Treble Town intro · 1 min</div>
            </div>
          </button>

          {/* ════ MOBILE PATH ════ */}
          <div className="gm-mobile-path">
            <div className="gm-path">
              <LearnNotesNode tutorialDone={tutorialDone} navigate={navigate} showCompanions={learnNotesIsActive} />
              <div className={`gm-connector ${tutorialDone ? "gm-connector-done" : "gm-connector-pending"}`} aria-hidden="true" />
              <PracticeNode navigate={navigate} />
              <div className={`gm-connector ${tutorialDone ? "gm-connector-done" : "gm-connector-pending"}`} aria-hidden="true" />
              {levelStatuses.map((entry, idx) => (
                <React.Fragment key={entry.level.id}>
                  {idx > 0 && (
                    <div className={`gm-connector gm-connector-${
                      levelStatuses[idx - 1].isCompleted ? "done" :
                      entry.isLoading ? "loading" :
                      entry.premiumLocked ? "locked" : "active"
                    }`} aria-hidden="true" />
                  )}
                  <LevelNode
                    level={entry.level} isCompleted={entry.isCompleted} isUnlocked={entry.isUnlocked}
                    isNext={entry.isNext} isPremiumLocked={entry.premiumLocked} isLoading={entry.isLoading}
                    navigate={navigate} premiumLoaded={premiumLoaded}
                    showCompanions={entry.level.id === activeLevelId}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ════ DESKTOP WINDING PATH ════ */}
          <DesktopWindingPath
            levelStatuses={levelStatuses} navigate={navigate} premiumLoaded={premiumLoaded}
            tutorialDone={tutorialDone} activeLevelId={activeLevelId} learnNotesIsActive={learnNotesIsActive}
          />

          {!isPremium && premiumLoaded && (
            <button className="gm-premium-cta" onClick={() => { trackEvent("premium_cta_click"); navigate("/pwp-paywall"); }}>
              🔓 Unlock all 10 songs · ₹299
            </button>
          )}
          {isPremium && premiumLoaded && (
            <button className="gm-premium-badge" onClick={() => { trackEvent("my_pass_badge_click"); navigate("/my-pass"); }}
              aria-label="View my premium pass details">
              ⭐ Premium · all unlocked
            </button>
          )}
          {!premiumLoaded && (
            <div className="gm-premium-checking">Checking your access…</div>
          )}
        </div>
      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');

.gm-root { min-height: 100vh; font-family: 'Nunito', sans-serif; color: #0f172a; position: relative; }
.gm-root * { box-sizing: border-box; }

.gm-bg { pointer-events: none; position: fixed; inset: 0; z-index: 0; overflow: hidden; }
.gm-bg-base { position: absolute; inset: 0; background: linear-gradient(180deg, #fffbeb 0%, #ffffff 60%, #f5f3ff 100%); }
.gm-blob { position: absolute; border-radius: 50%; filter: blur(100px); opacity: 0.3; animation: gmFloat 20s infinite ease-in-out; }
.gm-blob-1 { top: -5%; left: -8%; width: 400px; height: 400px; background: linear-gradient(135deg, #fde68a, #fbbf24); }
.gm-blob-2 { bottom: -10%; right: -5%; width: 360px; height: 360px; background: linear-gradient(135deg, #c4b5fd, #8b5cf6); animation-delay: 4s; }
@keyframes gmFloat { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(15px,-20px) scale(1.04); } }

.gm-header {
  position: sticky; top: 0; z-index: 100;
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; background: rgba(255,255,255,0.92); backdrop-filter: blur(14px);
  border-bottom: 1px solid #f1f5f9;
}
.gm-hdr-btn {
  width: 36px; height: 36px; border-radius: 50%; border: 2px solid #e2e8f0; background: #fff;
  font-size: 1rem; font-weight: 800; color: #0f172a; cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center;
}
.gm-hdr-btn:hover { border-color: #0f172a; transform: translateY(-1px); }

/* ✅ V4: Progress button — slightly wider to fit flame + icon */
.gm-hdr-progress-btn {
  width: auto; min-width: 36px; padding: 0 8px;
  flex-direction: column; gap: 1px;
}

.gm-hdr-title { display: flex; align-items: center; gap: 12px; font-family: 'Fredoka One', cursive; font-size: 0.95rem; color: #0f172a; }
.gm-hdr-stars { color: #d97706; }
.gm-hdr-progress { color: #7c3aed; }
.gm-hdr-right { display: flex; align-items: center; gap: 8px; }
.gm-hdr-pro {
  display: inline-flex; align-items: center; gap: 4px; padding: 6px 11px; height: 36px;
  border-radius: 18px; background: linear-gradient(135deg, #7c3aed, #6d28d9);
  border: 2px solid #5b21b6; font-family: 'Fredoka One', cursive; font-size: 0.72rem; color: #fff;
  cursor: pointer; transition: all 0.15s; box-shadow: 0 3px 0 #4c1d95, 0 4px 10px rgba(124,58,237,0.25);
}
.gm-hdr-pro:hover { transform: translateY(-1px); box-shadow: 0 4px 0 #4c1d95, 0 6px 14px rgba(124,58,237,0.35); }
.gm-hdr-pro:active { transform: translateY(1px); box-shadow: 0 1px 0 #4c1d95; }
.gm-hdr-pro-star { font-size: 0.85rem; line-height: 1; }
.gm-hdr-pro-text { letter-spacing: 0.05em; }

.gm-main { position: relative; z-index: 1; padding: 16px 16px 120px; max-width: 460px; margin: 0 auto; }
.gm-content { display: flex; flex-direction: column; align-items: stretch; gap: 14px; }

.gm-video-thumb {
  display: flex; align-items: center; gap: 12px; padding: 10px; border-radius: 16px;
  border: 1.5px solid #e2e8f0; background: #fff; cursor: pointer; transition: all 0.2s; width: 100%; text-align: left;
}
.gm-video-thumb:hover { border-color: #cbd5e1; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
.gm-video-thumb-poster {
  position: relative; width: 80px; height: 48px; flex-shrink: 0; border-radius: 10px;
  background-color: #0f172a; background-size: cover; background-position: center; overflow: hidden;
}
.gm-video-thumb-play {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,0.35); color: #fff; font-size: 0.95rem; padding-left: 3px;
}
.gm-video-thumb-text { flex: 1; min-width: 0; }
.gm-video-thumb-title { font-family: 'Fredoka One', cursive; font-size: 0.92rem; color: #0f172a; }
.gm-video-thumb-sub { font-size: 0.72rem; font-weight: 700; color: #64748b; margin-top: 1px; }

.gm-mobile-path { display: block; }
.gm-path { display: flex; flex-direction: column; align-items: center; gap: 0; padding: 8px 0; }

.gm-connector { width: 4px; height: 24px; margin: 0; border-radius: 2px; background: repeating-linear-gradient(to bottom, #cbd5e1 0px, #cbd5e1 5px, transparent 5px, transparent 10px); }
.gm-connector-done { background: repeating-linear-gradient(to bottom, #22c55e 0px, #22c55e 5px, transparent 5px, transparent 10px); }
.gm-connector-pending { background: repeating-linear-gradient(to bottom, #86efac 0px, #86efac 5px, transparent 5px, transparent 10px); opacity: 0.7; }
.gm-connector-loading { background: repeating-linear-gradient(to bottom, #f3e8ff 0px, #f3e8ff 5px, transparent 5px, transparent 10px); opacity: 0.6; }
.gm-connector-locked { background: repeating-linear-gradient(to bottom, #e9d5ff 0px, #e9d5ff 5px, transparent 5px, transparent 10px); }

.gm-node-wrap { position: relative; display: flex; justify-content: center; }
.gm-node-wrap-with-companions { margin: 6px 0; }

.gm-companions { position: absolute; inset: 0; pointer-events: none; z-index: 2; }
.gm-companion {
  position: absolute; width: var(--companion-size, 56px); height: var(--companion-size, 56px);
  object-fit: contain; top: 14px; filter: drop-shadow(0 4px 8px rgba(0,0,0,0.18)); user-select: none;
}
.gm-companion-pan { left: -8px; transform: translateX(-100%); animation: gmCompanionBobLeft 2.6s ease-in-out infinite; }
.gm-companion-wis { right: -8px; transform: translateX(100%); animation: gmCompanionBobRight 2.6s ease-in-out infinite; animation-delay: 0.4s; }
@keyframes gmCompanionBobLeft { 0%,100%{transform:translateX(-100%) translateY(0)} 50%{transform:translateX(-100%) translateY(-6px)} }
@keyframes gmCompanionBobRight { 0%,100%{transform:translateX(100%) translateY(0)} 50%{transform:translateX(100%) translateY(-6px)} }

.gm-node { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 6px 0; background: transparent; border: none; cursor: pointer; transition: transform 0.2s; position: relative; }
.gm-node:hover:not(:disabled) { transform: translateY(-2px); }
.gm-node:disabled { cursor: default; }

.gm-circle {
  position: relative; width: 96px; height: 96px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Fredoka One', cursive; flex-shrink: 0;
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s; border: 5px solid;
}
.gm-circle-num { font-size: 2.2rem; line-height: 1; }
.gm-circle-icon { font-size: 2.2rem; line-height: 1; }
.gm-circle-loading-spinner { animation: gmLoadingBounce 1.2s ease-in-out infinite; opacity: 0.7; }
@keyframes gmLoadingBounce { 0%,100%{transform:translateY(0);opacity:0.6} 50%{transform:translateY(-4px);opacity:1} }
.gm-circle-lock { position: absolute; bottom: -2px; right: -2px; width: 28px; height: 28px; border-radius: 50%; background: #7c3aed; border: 3px solid #fff; font-size: 0.85rem; display: flex; align-items: center; justify-content: center; }

.gm-circle-emoji { position: absolute; top: -8px; right: -6px; width: 32px; height: 32px; border-radius: 50%; background: #fff; border: 2.5px solid #fff; box-shadow: 0 3px 8px rgba(0,0,0,0.15); display: flex; align-items: center; justify-content: center; font-size: 1.15rem; line-height: 1; z-index: 3; animation: gmEmojiWiggle 5s ease-in-out infinite; transform-origin: center; }
.gm-circle-emoji-dim { opacity: 0.6; filter: grayscale(0.25); }
@keyframes gmEmojiWiggle { 0%,90%,100%{transform:rotate(0deg)} 93%{transform:rotate(-10deg)} 96%{transform:rotate(10deg)} }

.gm-circle-learn { background: linear-gradient(135deg,#22c55e,#16a34a); border-color: #15803d; color: #fff; box-shadow: 0 5px 0 #15803d, 0 8px 16px rgba(34,197,94,0.3); }
.gm-node:hover .gm-circle-learn { box-shadow: 0 7px 0 #15803d, 0 10px 20px rgba(34,197,94,0.4); }
.gm-circle-learn-done { background: linear-gradient(135deg,#4ade80,#22c55e); opacity: 0.85; }
.gm-circle-pulse-green { border-color: #22c55e; }
@keyframes gmLearnNodePulse { 0%,100%{box-shadow:0 5px 0 #15803d,0 0 0 0 rgba(34,197,94,0)} 50%{box-shadow:0 5px 0 #15803d,0 0 0 12px rgba(34,197,94,0.18)} }
.gm-node-learn-pulse .gm-circle-learn { animation: gmLearnNodePulse 2.4s infinite; }

.gm-circle-practice { background: linear-gradient(135deg,#38bdf8,#0284c7); border-color: #075985; color: #fff; box-shadow: 0 5px 0 #075985, 0 8px 16px rgba(2,132,199,0.28); }
.gm-node:hover .gm-circle-practice { box-shadow: 0 7px 0 #075985, 0 10px 20px rgba(2,132,199,0.38); }
.gm-node-practice .gm-node-song { color: #0369a1; }

.gm-circle-done { background: linear-gradient(135deg,#22c55e,#16a34a); border-color: #15803d; color: #fff; box-shadow: 0 5px 0 #15803d, 0 8px 16px rgba(34,197,94,0.25); }
.gm-node:hover .gm-circle-done { box-shadow: 0 7px 0 #15803d, 0 10px 20px rgba(34,197,94,0.35); }

.gm-circle-active { background: linear-gradient(135deg,#fbbf24,#f59e0b); border-color: #d97706; color: #0f172a; box-shadow: 0 5px 0 #d97706, 0 8px 20px rgba(251,191,36,0.4); }
.gm-node:hover .gm-circle-active { box-shadow: 0 7px 0 #d97706, 0 12px 28px rgba(251,191,36,0.5); }
.gm-circle-pulse { position: absolute; inset: -10px; border-radius: 50%; border: 4px solid #fbbf24; pointer-events: none; animation: gmPulseRing 1.8s ease-out infinite; }
@keyframes gmPulseRing { 0%{transform:scale(1);opacity:0.9} 100%{transform:scale(1.4);opacity:0} }

.gm-circle-unlocked { background: #fff; border-color: #cbd5e1; color: #475569; box-shadow: 0 5px 0 #cbd5e1, 0 6px 12px rgba(0,0,0,0.05); }
.gm-circle-locked { background: #f1f5f9; border-color: #e2e8f0; color: #94a3b8; box-shadow: 0 3px 0 #cbd5e1; opacity: 0.7; }

.gm-circle-loading { background: linear-gradient(135deg,#f3f4f6,#f9fafb); border-color: #e5e7eb; border-style: dashed; color: #9ca3af; opacity: 0.65; box-shadow: 0 3px 0 #e5e7eb, 0 4px 8px rgba(0,0,0,0.02); animation: gmLoadingPulse 1.8s ease-in-out infinite; }
@keyframes gmLoadingPulse { 0%,100%{opacity:0.65} 50%{opacity:0.8} }

.gm-circle-premium-locked { background: linear-gradient(135deg,#faf5ff,#fff); border-color: #c4b5fd; border-style: dashed; color: #7c3aed; box-shadow: 0 4px 0 #c4b5fd, 0 4px 12px rgba(124,58,237,0.08); opacity: 0.95; }
.gm-node:hover .gm-circle-premium-locked { opacity: 1; box-shadow: 0 5px 0 #a78bfa, 0 6px 16px rgba(124,58,237,0.15); }

.gm-circle-boss { width: 116px; height: 116px; border-width: 6px; }
.gm-circle-boss .gm-circle-num, .gm-circle-boss .gm-circle-icon { font-size: 2.6rem; }
.gm-node-boss { margin-top: 6px; }
.gm-node-boss .gm-circle-active, .gm-node-boss .gm-circle-unlocked { background: linear-gradient(135deg,#ef4444,#dc2626); border-color: #b91c1c; color: #fff; box-shadow: 0 6px 0 #991b1b, 0 10px 24px rgba(239,68,68,0.18); }
.gm-node-boss:hover .gm-circle-active, .gm-node-boss:hover .gm-circle-unlocked { box-shadow: 0 8px 0 #991b1b, 0 14px 32px rgba(239,68,68,0.25); }
.gm-circle-boss .gm-circle-emoji { width: 38px; height: 38px; font-size: 1.4rem; top: -10px; right: -8px; }

.gm-node-song { font-family: 'Fredoka One', cursive; font-size: 0.95rem; color: #0f172a; text-align: center; max-width: 200px; line-height: 1.15; }
.gm-node:disabled .gm-node-song { color: #94a3b8; }
.gm-node-boss .gm-node-song { font-size: 1.05rem; color: #b91c1c; }
.gm-node-boss:disabled .gm-node-song { color: #a78bfa; }

.gm-premium-cta { display: flex; align-items: center; justify-content: center; padding: 14px 18px; border-radius: 14px; background: linear-gradient(135deg,#7c3aed,#6d28d9); border: none; cursor: pointer; transition: all 0.2s; width: 100%; font-family: 'Fredoka One', cursive; font-size: 0.95rem; color: #fff; text-align: center; box-shadow: 0 4px 0 #5b21b6, 0 8px 20px rgba(124,58,237,0.18); margin-top: 8px; }
.gm-premium-cta:hover { transform: translateY(-2px); box-shadow: 0 6px 0 #5b21b6, 0 12px 28px rgba(124,58,237,0.25); }
.gm-premium-badge { padding: 12px 18px; border-radius: 14px; background: linear-gradient(135deg,#ede9fe,#f5f3ff); border: 2px solid #c4b5fd; font-family: 'Fredoka One', cursive; font-size: 0.92rem; color: #5b21b6; text-align: center; margin-top: 8px; cursor: pointer; transition: all 0.2s; width: 100%; }
.gm-premium-badge:hover { transform: translateY(-1px); border-color: #a78bfa; box-shadow: 0 4px 12px rgba(124,58,237,0.15); }
.gm-premium-checking { padding: 10px; text-align: center; font-size: 0.75rem; font-weight: 700; color: #94a3b8; margin-top: 8px; animation: gmLoadingPulse 1.8s ease-in-out infinite; }

.gm-dt-path { display: none; }
.gm-dt-down { width: 4px; height: 32px; margin: 10px auto 14px; border-radius: 2px; background: repeating-linear-gradient(to bottom, #cbd5e1 0px, #cbd5e1 6px, transparent 6px, transparent 12px); }
.gm-dt-row { display: flex; flex-direction: row; align-items: center; justify-content: center; }
.gm-dt-row-rtl { flex-direction: row-reverse; }
.gm-dt-h-conn { width: 48px; height: 5px; flex-shrink: 0; border-radius: 2.5px; background: repeating-linear-gradient(to right, #cbd5e1 0px, #cbd5e1 8px, transparent 8px, transparent 16px); transition: all 0.3s; }
.gm-dt-h-conn-done { background: repeating-linear-gradient(to right, #22c55e 0px, #22c55e 8px, transparent 8px, transparent 16px); }
.gm-dt-h-conn-loading { background: repeating-linear-gradient(to right, #f3e8ff 0px, #f3e8ff 8px, transparent 8px, transparent 16px); opacity: 0.6; }
.gm-dt-h-conn-prem { background: repeating-linear-gradient(to right, #c4b5fd 0px, #c4b5fd 8px, transparent 8px, transparent 16px); }
.gm-dt-turn { position: relative; height: 40px; width: 100%; max-width: 480px; margin: 0 auto; }
.gm-dt-turn::after { content: ''; position: absolute; top: 0; width: 4px; height: 100%; border-radius: 2px; background: repeating-linear-gradient(to bottom, #cbd5e1 0px, #cbd5e1 6px, transparent 6px, transparent 12px); }
.gm-dt-turn-right::after { right: 94px; }
.gm-dt-turn-left::after { left: 94px; }
.gm-dt-boss-zone { display: flex; flex-direction: column; align-items: center; gap: 8px; margin-top: 4px; }
.gm-dt-boss-tag { font-family: 'Fredoka One', cursive; font-size: 0.82rem; color: #dc2626; padding: 4px 16px; border-radius: 99px; background: #fef2f2; border: 1.5px solid #fca5a5; }

@media (min-width: 768px) {
  .gm-mobile-path { display: none; }
  .gm-dt-path { display: block; }
  .gm-main { max-width: 640px; padding: 24px 32px 140px; }
  .gm-companion-pan { left: -14px; }
  .gm-companion-wis { right: -14px; }
}

.gm-intro-overlay { position: fixed; inset: 0; z-index: 10000; background: rgba(15,23,42,0.88); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: gmOverlayFadeIn 0.25s ease-out; }
@keyframes gmOverlayFadeIn { from{opacity:0} to{opacity:1} }
.gm-intro-modal { position: relative; width: 100%; max-width: 720px; border-radius: 20px; background: #0f172a; border: 3px solid #fbbf24; box-shadow: 0 20px 60px rgba(0,0,0,0.5); overflow: hidden; animation: gmModalPop 0.35s cubic-bezier(0.34,1.56,0.64,1); }
@keyframes gmModalPop { from{opacity:0;transform:scale(0.92) translateY(12px)} to{opacity:1;transform:scale(1) translateY(0)} }
.gm-intro-close { position: absolute; top: 10px; right: 10px; z-index: 10; width: 36px; height: 36px; border-radius: 10px; background: rgba(255,255,255,0.95); border: 2px solid #0f172a; font-family: 'Fredoka One', cursive; font-size: 1.4rem; line-height: 1; color: #0f172a; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 3px 0 0 #0f172a; transition: all 0.15s; }
.gm-intro-close:hover { transform: translateY(-1px); box-shadow: 0 4px 0 0 #0f172a; }
.gm-intro-video-wrap { width: 100%; aspect-ratio: 16/9; background: #000; }
.gm-intro-video { width: 100%; height: 100%; display: block; background: #000; }

@media (max-width: 480px) {
  .gm-circle { width: 80px; height: 80px; border-width: 4px; }
  .gm-circle-num, .gm-circle-icon { font-size: 1.8rem; }
  .gm-circle-boss { width: 100px; height: 100px; border-width: 5px; }
  .gm-circle-boss .gm-circle-num, .gm-circle-boss .gm-circle-icon { font-size: 2.2rem; }
  .gm-circle-lock { width: 24px; height: 24px; font-size: 0.72rem; border-width: 2.5px; }
  .gm-node-song { font-size: 0.85rem; max-width: 180px; }
  .gm-node-boss .gm-node-song { font-size: 0.95rem; }
  .gm-video-thumb-poster { width: 64px; height: 38px; }
  .gm-intro-overlay { padding: 12px; }
  .gm-intro-modal { max-width: 100%; border-radius: 16px; }
  .gm-intro-close { width: 34px; height: 34px; top: 8px; right: 8px; }
  .gm-hdr-pro { padding: 4px 9px; font-size: 0.66rem; height: 32px; }
  .gm-hdr-pro-star { font-size: 0.75rem; }
  .gm-hdr-right { gap: 6px; }
  .gm-circle-emoji { width: 28px; height: 28px; font-size: 1rem; top: -6px; right: -4px; }
  .gm-circle-boss .gm-circle-emoji { width: 32px; height: 32px; font-size: 1.2rem; }
  .gm-companion { width: 44px !important; height: 44px !important; top: 12px; }
  .gm-companion-pan { left: -4px; }
  .gm-companion-wis { right: -4px; }
}
@media (max-width: 360px) {
  .gm-circle { width: 72px; height: 72px; }
  .gm-circle-num, .gm-circle-icon { font-size: 1.6rem; }
  .gm-circle-boss { width: 88px; height: 88px; }
  .gm-circle-boss .gm-circle-num, .gm-circle-boss .gm-circle-icon { font-size: 1.9rem; }
  .gm-node-song { font-size: 0.8rem; }
  .gm-circle-emoji { width: 26px; height: 26px; font-size: 0.92rem; }
  .gm-companion { width: 40px !important; height: 40px !important; }
}
`;